import { useState, useRef, useEffect } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";

// ─── Firebase Config ─────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyA6MZ7fw90EVJMTcSjQFPa3SQHGk8Aev6s",
  authDomain: "project-catalog-4c6a9.firebaseapp.com",
  projectId: "project-catalog-4c6a9",
  storageBucket: "project-catalog-4c6a9.firebasestorage.app",
  messagingSenderId: "535017232500",
  appId: "1:535017232500:web:15d16abfad139dac9a9921",
  measurementId: "G-TF5FT8RM7T",
};
const firebaseApp = initializeApp(FIREBASE_CONFIG);
const db = getFirestore(firebaseApp);

// ─── Firestore helpers ───────────────────────────────────────────────────────
const APP_DOC = doc(db, "app_data", "main");

function saveToFirestore(solutions, subAdmins) {
  setDoc(APP_DOC, { solutions, subAdmins }).catch(() => {});
}

// ─── Admin Credentials ───────────────────────────────────────────────────────
const ADMIN_EMAIL = "s.marouane@dxc.com";
const ADMIN_PASSWORD = "Csm@dxc"; // Fill in your password

// ─── DXC Brand Tokens ──────────────────────────────────────────────────────
const DXC = {
  orange:        "#FF6200",
  orangeHover:   "#E55A00",
  navy:          "#1A1A2E",
  navyMid:       "#16213E",
  white:         "#FFFFFF",
  offWhite:      "#F5F5F5",
  lightGray:     "#E8E8E8",
  textDark:      "#1A1A1A",
  textMid:       "#555555",
  textLight:     "#888888",
  green:         "#16A34A",
  greenBg:       "#F0FDF4",
  greenBorder:   "#BBF7D0",
  blue:          "#2563EB",
  blueBg:        "#EFF6FF",
  blueBorder:    "#BFDBFE",
  delivery:      { bg: "#FFF7ED", text: "#EA580C", border: "#FED7AA" },
  sourcing:      { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" },
  qualification: { bg: "#F0FDF4", text: "#16A34A", border: "#BBF7D0" },
};

// ─── Simple neutral tag chip ─────────────────────────────────────────────
function TagChip({ label }) {
  return (
    <span style={{
      background: DXC.offWhite,
      color: DXC.textMid,
      border: `1px solid ${DXC.lightGray}`,
      fontSize: 10, fontWeight: 600,
      padding: "3px 9px", borderRadius: 4,
      letterSpacing: 0.3, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

// ─── Solutions Data ────────────────────────────────────────────────────────
const INITIAL_SOLUTIONS = [
  // ── CONSULTING ────────────────────────────────────────────────────────────
  {
    id: 1, rank: "01", category: "Consulting",
    title: "AI Use Case Radar",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "Cross-industry intelligence tool that scans and prioritizes emerging AI trends and use cases to benchmark innovation and align strategic roadmap decisions.",
    popup: {
      headline: "AI Use Case Radar",
      deliverable: "MVP in 6-10 Weeks",
      stage: "Qualification",
      description: "A sector-agnostic intelligence tool that continuously scans and prioritizes emerging AI use cases and market trends. Enables organizations to benchmark innovation, identify opportunities, and align their AI strategy with real-world advancements.",
      businessValue: [
        "Faster deal conversion",
        "Higher ROI per use case",
        "Reduced time-to-value",
      ],
      personas: [
        "Pre-Sales Consultant - From Preparation to Presentation in Minutes",
        "AI Project Lead - From Intuition to Operational Roadmap",
        "Business Developer - From Cold Call to Qualified Conversation",
      ],
      featuresDone: [
        "AI-Assisted Client Onboarding",
        "Use Case Catalogue (1,000+)",
        "Industry Filters & Semantic Search",
        "Interactive Radar Chart (5 Axes)",
        "Weighted Scoring Engine (4 Criteria)",
        "One-Click PDF Export",
        "AI Act Compliance Checker",
        "Contextual Recommendation",
        "AI Roadmap Generation",
        "Real-Time Knowledge Base Enrichment",
        "Business Case Simulation (CAPEX/OPEX)",
      ],
      featuresOngoing: [
        "Multi-Agent Orchestration",
        "Fully Autonomous Agents for Client Search",
        "Pitch Generation",
        "Weekly Intelligence Scrapping Pipeline",
        "Enterprise Roadmap Generator",
        "User Feedback",
        "JWT-based Secure Authentication",
        "Observability and Monitoring",
        "LLM Router",
      ],
    },
  },
  {
    id: 2, rank: "02", category: "Consulting",
    title: "Assessment Advisor",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "Comprehensive evaluation of an organization's data & AI landscape covering sources, structure, usage and risks to build strategy and align with business objectives.",
    popup: {
      headline: "Assessment Advisor",
      deliverable: "Full deliverable in 1-3 Weeks",
      stage: "Qualification",
      description: "Provides a comprehensive evaluation of your organization's data and AI maturity. It evaluates infrastructure, governance, talent gaps, and technology alignment - delivering a clear, prioritized action plan and strategic roadmap.",
      businessValue: [
        "Standardization of digital maturity assessments (Data Governance & AI) into a repeatable process",
        "Actionable insights to guide clients' digital transformation decisions",
        "Enhanced consulting efficiency through an AI Agent generating personalized analyses and recommendations",
      ],
      personas: [
        "External clients completing assessments via invitation links",
        "Internal clients assessed with consultant support",
        "Administrators managing assessments, invitations, and analytics dashboards",
      ],
      featuresDone: [
        "Structured multi-domain assessment covering Data Governance & AI",
        "Sequential question definition interface",
        "SSO Authentication Keycloak",
        "Admin interface: questions, choices, scoring",
        "Analytics dashboard with maturity score",
        "Multilingual support (FR / EN / AR)",
        "Consolidation of invitation links for external assessments",
        "LLM Benchmark: Mistral, Groq, OpenRouter",
        "Client-side and admin-side AI chatbots with streaming",
        "Admin chatbot with 3-level context and per-context history",
        "AI Recommendations with editable domain insights panel",
        "AI-generated per-client narrative PDF report",
      ],
      featuresOngoing: [
        "Reworked Analytics Dashboard",
        "AI replies in the user's selected language (EN / FR / AR)",
        "Build with AI copilot for the assessment builder",
        "Visual avatar for the chat assistant",
        "Consultant-owned client scoping across analytics, chat, and reports",
        "Production-ready deployment with AI agent secured behind web app",
        "Comprehensive test suite across frontend, backend, and agent",
        "Full multilingual AI coverage across all chat modes",
        "Language-aware context preserved on mid-conversation switch",
        "Durable 3-level chat history (survives agent restarts)",
        "Editable AI drafts with full edit history and rollback",
      ],
    },
  },
  {
    id: 3, rank: "03", category: "Consulting",
    title: "ROI Simulator",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "Measures and communicates business value of data and AI investments with customizable financial models, scenario comparison, and sensitivity analysis.",
    popup: {
      headline: "ROI Simulator",
      deliverable: "VB & KPIs in 2 weeks - Full deliverable in 1-2 months",
      stage: "Qualification",
      description: "Measures and communicates the business value of data and AI investments in financial and operational terms. Business value and KPIs are defined within 2 weeks, giving leadership the evidence they need to commit to AI transformation with confidence.",
      businessValue: [
        "Reduces the risk of launching unprofitable projects",
        "Accelerates decision-making for managers",
        "Provides a quick estimation of ROI, payback, and savings",
        "Transforms an AI use case into a clear financial projection in seconds",
      ],
      personas: [
        "Decision Makers",
        "Business & Strategy Teams",
        "Finance Teams",
      ],
      featuresDone: [
        "Use case input",
        "Automatic domain detection",
        "Dynamic form",
        "Intelligent agent implementation",
        "Visual KPI cards (ROI, Payback, NPV, Savings)",
        "Secure authentication",
        "Initial financial estimation",
        "Chatbot for financial recommendations",
        "Reporter Agent: business-oriented narrative summary",
      ],
      featuresOngoing: [
        "Full executive report with detailed financial analysis",
        "AI-generated personalized recommendations based on domain",
        "Optimization of agent performance and response time",
        "Application optimization",
        "Data structuring to support frequent updates",
        "Full cloud deployment (Docker + automated CI/CD)",
        "Complete technical documentation",
        "End-to-end testing and final optimization",
      ],
    },
  },

  // ── DELIVERY ──────────────────────────────────────────────────────────────
  {
    id: 4, rank: "04", category: "Delivery",
    title: "Data Health",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "End-to-end data quality platform with automated rule-based validation, real-time monitoring & alerts, and root-cause diagnostics across all systems.",
    popup: {
      headline: "Data Health",
      deliverable: "Full deliverable in 6 Weeks",
      stage: "Delivery",
      description: "An end-to-end platform that ensures data quality across all your systems. From discovery to action, Data Health gives you full control with built-in compliance, automation and intelligence - so your teams always work from trusted data.",
      businessValue: [
        "Leverage data profiling, classification, and lineage to improve data understanding and traceability",
        "Ensure reliable data through full visibility and quality scoring",
        "Detect and monitor anomalies to improve data quality",
        "Automate creation of data quality rules using AI agent",
        "Automated regulatory compliance",
        "Risk and penalty reduction",
      ],
      personas: [
        "IT & Data Teams",
        "Business Users",
        "Executive Management",
        "Risk, Compliance & Audit Teams",
      ],
      featuresDone: [
        "Complete multi-source data import pipeline",
        "Data quality scores (6 dimensions + overall score)",
        "Data profiling: data analysis and type detection",
        "AI agent for matching GDPR/Data Act articles with data quality rules",
        "KPI dashboard and compliance reporting",
        "Precise data anomaly detection through detailed analysis",
        "Data classification: automated semantic detection and business identification",
        "Data lineage tracking and visualization",
        "AI agent for creating data quality rules and generating test data",
        "Financial risk scoring",
      ],
      featuresOngoing: [
        "Alerts to monitor data quality",
        "Integration of proactive AI to suggest rules",
        "AI decision explainability (XAI) + rule suggestions",
        "Scoring by business domain",
        "AI evaluation (F1-Score)",
        "Full integration of all modules",
        "Production deployment",
        "Documentation and testing",
      ],
    },
  },
  {
    id: 5, rank: "05", category: "Delivery",
    title: "Intelligent Analytics",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "Transforms raw data into actionable insights using ML, NLP and advanced statistical models, enabling proactive decision-making and uncovering hidden patterns in real time.",
    popup: {
      headline: "Intelligent Analytics",
      deliverable: "Full deliverable in 1-2 Weeks",
      stage: "Delivery",
      description: "Transforms raw data into actionable insights using ML, NLP and advanced statistical models. Enables proactive decision-making by uncovering hidden patterns in real time and explaining results contextually.",
      businessValue: [
        "Faster Time to Insight",
        "Multi-Sector & Scalable",
        "Analytics for Non-Technical Users",
        "Explainable & Transparent AI",
      ],
      personas: [
        "Managers & Decision Makers",
        "Analysts & Data Teams",
      ],
      featuresDone: [
        "Secure and controlled user access",
        "Structured projects with seamless data ingestion",
        "Automatic sector detection for faster setup",
        "Conversational AI chatbot",
        "Context-aware AI with memory",
        "Auto-generated KPIs, charts, and dashboards",
        "AI-powered data preparation with anomaly detection and smart cleaning",
        "Multi-domain support across Airport, Factory etc.",
        "Smart grid with real-world dimensions and auto-calculated layout",
        "Real-time monitoring with live tracking and proactive alerts",
        "Interactive dashboards that adapt instantly to user queries",
      ],
      featuresOngoing: [
        "Smart predictions on key business metrics (churn, revenue, risk)",
        "Industry-focused AI in Transport and Retail",
        "Explains predictions with actionable recommendations",
        "Intelligent data validation and automatic model selection",
        "Reinforced predictions and expanded sector coverage (Finance, Manufacturing & Public)",
        "Multilingual chatbot supporting FR, AR, ENG",
        "Fully refined system with all enhancements integrated",
      ],
    },
  },
  {
    id: 6, rank: "06", category: "Delivery",
    title: "AI Assistant",
    tags: ["Interface", "Agentic", "Pilot"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "Conversational AI assistant that interacts naturally with users, understands intent, and executes predefined tasks across HR platforms and communication channels.",
    popup: {
      headline: "AI Assistant",
      deliverable: "POC Live March 2026 - MVP April 2026",
      stage: "Delivery",
      description: "L'assistant IA fournit des reponses instantanees et fiables aux questions metier courantes, liberant les equipes operationnelles pour des taches a plus forte valeur ajoutee. Concu pour etre deployable a l'echelle mondiale et adaptable a tout domaine d'activite.",
      businessValue: [
        "Instant and reliable answers to common business questions",
        "Frees operational teams for higher-value tasks",
        "Globally deployable and adaptable to any business domain",
      ],
      personas: [
        "Business teams",
        "All-level collaborators",
        "Operational managers needing instant and reliable answers on processes and internal information",
      ],
      featuresDone: [
        "Semantic search & knowledge discovery",
        "Conversational AI interface",
        "Knowledge base management",
        "Role-based access control",
        "Conversation history",
        "DXC theme UI unification",
        "Pilot environment deployment",
        "Streaming functionality",
        "Multi-format upload (Word, Excel, PPT, images)",
        "Security improvements",
        "Response time optimization (50s -> 7-8s)",
      ],
      featuresOngoing: [
        "File visualization",
        "Codebase refactoring",
        "Response adaptation and improvement",
        "Extension to other use cases",
        "Domain personalization",
        "Voice interaction",
        "RGPD / AI Act compliance",
        "Scalable worldwide product",
        "Multi-tenant onboarding",
        "API integrations",
      ],
    },
  },
  {
    id: 7, rank: "07", category: "Delivery",
    title: "Digital Twin",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "Visual digital twin platform enabling teams to configure, monitor and optimize industrial environments with real-time KPIs, smart grid layout and natural language interaction.",
    popup: {
      headline: "Digital Twin",
      deliverable: "Full deliverable in 6-10 Weeks",
      stage: "Delivery",
      description: "A visual platform for building and monitoring industrial digital twins. Teams configure environments through a 5-step flow (Configure, Place, Connect, KPIs, Monitor) with drag-and-drop or natural language placement, supporting Airport, Factory and Warehouse domains.",
      businessValue: [
        "Better Operational Decisions - Real-time visibility on KPIs, equipment status and anomalies",
        "Reduced Technical Complexity - Simplify data-source connection, KPI mapping and layout configuration",
        "Cross-Team Alignment - Shared view for business, operations and technical teams",
        "Fast Digital Twin Deployment - Configure and create industrial digital twins fast",
      ],
      personas: [
        "Business Decision Makers - Access clear indicators to support strategic decisions",
        "Operations Managers - Monitor real-time performance, equipment status and alerts",
        "Data & Analytics Teams - Analyze KPI trends, anomalies and operational data",
      ],
      featuresDone: [
        "5-step flow: Configure, Place, Connect, KPIs, Monitor",
        "3 domains: Airport, Factory, Warehouse",
        "Smart grid with real dimensions and auto-calculated layout",
        "Drag-and-drop or text prompt (natural language) placement",
        "Draw connections between components",
        "Set KPIs with green/amber/red thresholds",
        "Live monitoring + real-time alerts",
        "Option to modify area directly on the grid",
        "Option to modify component dimensions",
        "CRUD and ability to have multiple digital twins",
        "NLQ component enabling users to add custom components",
        "Multi-tenant authentication system",
        "Database connection wizard",
      ],
      featuresOngoing: [
        "Export and share functionalities",
        "Upgrade NLQ component generation",
        "AI-driven PDF report generation",
        "Adding the data center domain",
        "Ability to add new domains (not just predefined ones)",
        "Upgrading the user interface",
      ],
    },
  },
  {
    id: 8, rank: "08", category: "Delivery",
    title: "AO Handler",
    tags: ["Interface", "Agentic", "Prototype"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "Discovers public and private tenders, evaluates eligibility, and auto-generates compliant proposal drafts accelerating bid cycles and increasing win rates.",
    popup: {
      headline: "AO Handler",
      deliverable: "Full deliverable in 6-10 Weeks",
      stage: "Sourcing",
      description: "Cross-industry intelligence tool that discovers tenders, evaluates eligibility, and auto-generates winning proposals. Streamlines the entire bid management lifecycle with AI - from discovery to submission.",
      businessValue: [
        "Dramatically reduce time spent on tender responses",
        "Increase win rate by delivering higher-quality bids",
        "Risk recommendation to stay alert to critical clauses and avoid costly mistakes",
        "Ensure compliance & flag risks automatically",
      ],
      personas: [
        "BID Managers",
        "Delivery teams",
      ],
      featuresDone: [
        "Intelligent Document Ingestion",
        "CPS upload workflow",
        "Scraping workflow",
        "Finalization of the scraping pipeline and smart sorting agent",
        "Full integration of analysis and response generation workflow",
        "Ready AO Handler: auto import, scrape, and structure CPS documents",
      ],
      featuresOngoing: [
        "Multitenant architecture, data isolation, and granular access control",
        "CPS analysis agent (via upload)",
        "Personalized response generation agent (company context + CPS analysis)",
        "Scalable, reliable platform for end-to-end tender management",
      ],
    },
  },
  {
    id: 9, rank: "09", category: "Delivery",
    title: "Incidents Management",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "AI-powered incident detection and resolution platform that streamlines response workflows and reduces mean time to resolution across IT operations.",
    popup: {
      headline: "AI-Powered Incident Processing Automation",
      deliverable: "Full deliverable in 8-12 Weeks",
      stage: "Delivery",
      description: "End-to-end automation of ISTM emails with complete traceability, multilingual responses and native ServiceNow integration. Dramatically reduces MTTR and manual triage effort through intelligent agent orchestration.",
      businessValue: [
        "End-to-end automation of ISTM emails",
        "Reduction of errors & manual follow-ups",
        "Complete traceability",
        "Multilingual responses",
        "Native ServiceNow integration & automated ticket synchronization",
      ],
      personas: [
        "Dealers & partners",
        "ISTM teams & IT managers",
        "Operators & fleet managers",
      ],
      featuresDone: [
        "Agent 1-7: Complete email pipeline",
        "Multilingual understanding of requests",
        "Reading and extracting information from tickets",
        "Multi-class incident classification",
        "Key data extraction",
        "Analysis of the request context",
        "Automatic response or intelligent follow-up",
        "Automatic case creation and tracking",
        "Connection to business systems (ServiceNow, Outlook, CRM)",
        "Multi-platform interoperability",
        "Real-time business data access",
      ],
      featuresOngoing: [
        "Business Rules Engine",
        "Domain-specific configuration (Automotive, Retail, Telecom, Transport)",
        "Automatic response enrichment",
        "Advanced ITSM automation",
        "Intelligent request prioritization",
        "Agent orchestration & intelligent coordination",
        "Sentiment analysis & automatic escalation",
        "Advanced human-in-the-loop",
        "Multi-brand & multi-region scaling",
        "MCP connector marketplace",
        "Predictive AI for recurring failures",
        "Automated GDPR compliance",
      ],
    },
  },
  {
    id: 10, rank: "10", category: "Delivery",
    title: "Marketing Agents",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "28/07/2026",
    summary: "Intelligent AI agents that automate and optimize marketing workflows from campaign planning to content generation and performance tracking.",
    popup: {
      headline: "Marketing Agents",
      deliverable: "Full deliverable in 6-10 Weeks",
      stage: "Sourcing",
      description: "A suite of autonomous AI agents that handle segmentation, campaign personalization, content generation, and performance optimization - freeing marketing teams to focus on strategy.",
      businessValue: [
        "Automate repetitive marketing workflows",
        "Improve campaign personalization and targeting",
        "Accelerate content generation across channels",
      ],
      personas: [
        "Marketing Teams",
        "Campaign Managers",
        "Content Strategists",
      ],
      featuresDone: [
        "Audience segmentation AI",
        "Personalized content generation",
        "Campaign automation",
      ],
      featuresOngoing: [
        "Performance analytics",
        "Multi-channel orchestration",
      ],
    },
  },

  // ── INNOVATION ────────────────────────────────────────────────────────────
  {
    id: 11, rank: "11", category: "Innovation",
    title: "AI Watch",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "20/04/2026", deliveryDate: "14/04/2026",
    summary: "Strategic intelligence platform that analyzes tech trends, startups, and innovations to generate insights for proactive innovation and investment decisions.",
    popup: {
      headline: "AI Watch - Veille Technologique",
      deliverable: "V2 MVP Live - V3 in planning",
      stage: "Delivery",
      description: "Agent strategique analysant en continu les tendances technologiques, startups, brevets, levees de fonds et innovations sectorielles. Il synthetise l'information critique et genere des rapports a forte valeur decisionnelle.",
      businessValue: [
        "Transform raw tech data into strategic intelligence",
        "24/7 automated monitoring of AI trends, startups, patents and funding rounds",
        "AI-powered summaries & signal detection for CTOs, Innovation Managers and Strategy Directors",
      ],
      personas: [
        "CTO - Evaluate & justify AI investments",
        "Innovation Manager - Competitive benchmark & reports",
        "Strategy Director - National AI roadmap & regulation",
      ],
      featuresDone: [
        "Automated news collection",
        "AI article summarization",
        "Industry categorization",
        "Weak signal detection",
        "Startup & entity extraction",
        "Weekly newsletter digest",
        "Reports system (CRUD)",
        "PDF export (jsPDF)",
        "Data Preview + CSV",
        "Trends page (AI clusters)",
        "50 articles/generate",
        "15 DXC solutions mapped",
      ],
      featuresOngoing: [
        "Competitive radar (interactive)",
        "Tech maturity scoring",
        "Sector-specific AI analysis",
        "Automated benchmarking",
        "Key actors mapping",
        "Funding rounds tracking",
        "Patent & publication monitor",
        "6-12 month predictions",
        "Investment opportunities",
        "Executive COMEX reports",
        "Full BI Power integration",
        "Real-time alert engine",
        "Multi-tenant SaaS mode",
      ],
    },
  },
  {
    id: 12, rank: "12", category: "Innovation",
    title: "StartUp Connect AI",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "20/04/2026", deliveryDate: "14/04/2026",
    summary: "AI-powered engine that discovers startups and matches them to business needs, generating a relevant and explainable shortlist for strategic partnership decisions.",
    popup: {
      headline: "StartUp Connect AI",
      deliverable: "Full deliverable in 4-6 Weeks",
      stage: "Sourcing",
      description: "Continuously scans the global startup ecosystem and matches emerging companies to your specific innovation needs using AI fit scoring and strategic alignment analysis.",
      businessValue: [
        "Faster scouting of relevant Moroccan startups",
        "Better fit between business need and solution",
        "Explainable ranking for quicker decisions",
        "Reusable DXC startup knowledge base",
        "Stronger data quality and vendor readiness",
      ],
      personas: [
        "Innovation Manager / Digital Transformation Lead",
        "Procurement / Vendor Manager",
        "CTO / Enterprise Architect / CISO",
        "DXC Consultant / Account Manager",
      ],
      featuresDone: [
        "Onboarding questionnaire",
        "Guided search & filters",
        "Startup cards",
        "Startup detail page",
        "Admin CRUD + Excel import/export",
        "Conversational search + RAG",
        "Hybrid matching (SQL + vector)",
        "Multi-criteria scoring",
        "Explainable shortlist",
      ],
      featuresOngoing: [
        "Shortlists + sharing",
        "Advanced scoring (TRL / DXC)",
        "Duplicate detection",
        "Data enrichment (site / LinkedIn)",
        "Feedback loop",
        "Dashboard analytics",
        "RBAC + audit logs",
        "Security hardening",
        "Monitoring & quality KPIs",
        "CI/CD + documentation",
        "DXC-ready deployment",
      ],
    },
  },
  {
    id: 13, rank: "13", category: "Innovation",
    title: "POC Workflow Tool",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "20/04/2026", deliveryDate: "14/04/2026",
    summary: "AI platform that structures and manages the innovation cycle from business needs to accelerate POC decision-making and streamline the proof-of-concept lifecycle.",
    popup: {
      headline: "POC Workflow Tool - IPM Flow",
      deliverable: "Full deliverable in 3-5 Weeks",
      stage: "Qualification",
      description: "AI-powered platform that turns business needs into high-value, PoC-ready recommendations accelerating decision-making and innovation. Structures and manages the full innovation lifecycle from idea capture through feasibility analysis to go/no-go POC decisions.",
      businessValue: [
        "Turns business needs into high-value, PoC-ready recommendations",
        "Accelerates decision-making and innovation",
        "Structures the full innovation lifecycle",
      ],
      personas: [
        "CIO / CDO / Innovation Manager",
        "Business Sponsor / PoC Architect",
      ],
      featuresDone: [
        "Natural Language Business Need Capture",
        "AI-Assisted Suggestions & Auto-Classification",
        "Semantic Duplicate Detection",
        "SG-1 Validation",
        "Analysis Cards per Solution",
        "Discovery & SG-2 Validation",
        "Confidence Levels on AI-Detected Tags",
        "Streamlit Dashboard - Business Needs Progress Tracking",
        "Multi-Criteria Scoring (IVI) & Ranking",
        "SG-3 Validation",
      ],
      featuresOngoing: [
        "Technical & Organizational Recommendations",
        "KPI Definition",
        "SG-4 Validation & Final Reco Report (PDF/DOCX)",
        "UX/UI Improvements",
        "Aspect Modeling on the SG correction loop",
        "Conversational Stage Gate agents",
        "LLM Upgrade: Groq to OpenAI",
        "Multi-tenant & Client Portal",
        "Advanced Analytics & Innovation Tracking",
        "Integration with DXC AI Portfolio & Bid Automation",
      ],
    },
  },
  {
    id: 14, rank: "14", category: "Innovation",
    title: "Sandbox AI",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "14/04/2026",
    summary: "Secure experimentation environment for testing and validating AI models and use cases in isolation before production deployment across enterprise systems.",
    popup: {
      headline: "Sandbox AI",
      deliverable: "Full deliverable in 2-4 Weeks",
      stage: "Qualification",
      description: "AI Sandbox is a unified testing environment for AI teams. It provides automated KPI tracking, real-time dashboards, and LLM-powered evaluation reports that replace manual review and accelerate agent quality at scale.",
      businessValue: [
        "Unified testing environment for AI teams",
        "Automated KPI tracking and real-time dashboards",
        "LLM-powered evaluation reports replacing manual review",
        "Accelerate agent quality at scale",
      ],
      personas: [
        "AI delivery teams",
        "Business and product teams",
        "Governance and leadership teams",
      ],
      featuresDone: [
        "Centralized database of datasets",
        "Run multiple ML models in one click",
        "Track Models training in real time",
        "Auto-generated report",
        "PDF report download",
        "Improved ML reports with AI insights",
        "Test AI agents",
        "Calculate Technical KPIs",
        "Calculate quality KPIs",
        "Display KPIs in a Dashboard",
        "Recommendations to improve the agent",
      ],
      featuresOngoing: [
        "Improve Agent test reports and Dashboard",
        "Add Workflows testing module",
        "Improve the overall UI/UX",
        "Add authentication layer",
        "Agent comparison by version",
        "Deploy on DXC internal network",
        "Enhanced Security",
        "Handle multiple users at the same time",
        "On-Prem Security Hardening",
      ],
    },
  },
  {
    id: 15, rank: "15", category: "Delivery",
    title: "Smart Collect Hub",
    tags: ["Interface", "Agentic", "MVP"],
    mvpDate: "14/04/2026", deliveryDate: "14/04/2026",
    summary: "AI-powered collection platform eliminating manual follow-ups, accelerating cash flow, and giving finance teams full visibility from invoice to payment.",
    popup: {
      headline: "Smart Collect Hub",
      deliverable: "POC Live March 2026 - MVP April 2026",
      stage: "Sourcing",
      description: "AI-powered collection platform that eliminates manual follow-ups, accelerates cash flow, and gives finance teams full visibility from invoice to payment.",
      businessValue: [
        "Eliminate manual follow-ups in collections",
        "Accelerate cash flow",
        "Full visibility from invoice to payment",
      ],
      personas: [
        "Finance Teams",
        "Accounts Receivable Managers",
        "CFOs & Financial Controllers",
      ],
      featuresDone: [
        "Invoice creation & PDF generation",
        "AI reminder engine (GPT-4o-mini)",
        "Live dashboard & 2-way email inbox",
        "Payment ledger & multi-currency analytics",
      ],
      featuresOngoing: [
        "Auto-escalation rules",
        "Multi-user roles",
        "Auto email sync",
        "DSO & BI reporting",
        "Self-service portal",
        "AI dispute resolution",
        "Predictive payment scoring",
      ],
    },
  },
];

// ─── DXC Logo ──────────────────────────────────────────────────────────────
function DXCLogo({ size = 120 }) {
  return (
    <img src="/solutions/DXC.png" alt="DXC Technology"
      style={{ width: size, objectFit: "contain" }} />
  );
}

// ─── Admin Login Page ──────────────────────────────────────────────────────
function AdminLoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      localStorage.setItem("dxc_admin", "true");
      localStorage.setItem("dxc_admin_email", email);
      onLogin();
    } else {
      loadSubAdminsFromFirestore().then((subAdmins) => {
        const match = subAdmins.find((a) => a.email === email.trim().toLowerCase() && a.password === password);
        if (match) {
          localStorage.setItem("dxc_admin", "true");
          localStorage.setItem("dxc_admin_email", match.email);
          onLogin();
        } else {
          setError("Invalid credentials");
        }
      });
    }
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: DXC.offWhite, fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <form onSubmit={handleSubmit} style={{
        background: DXC.white, borderRadius: 16, padding: "2.5rem 2rem",
        boxShadow: "0 8px 32px rgba(0,0,0,0.10)", width: 360, maxWidth: "90vw",
        borderTop: `4px solid ${DXC.orange}`,
      }}>
        <h2 style={{ margin: "0 0 0.25rem", color: DXC.navy, fontSize: "1.25rem", fontWeight: 800 }}>
          Admin Login
        </h2>
        <p style={{ margin: "0 0 1.5rem", color: DXC.textLight, fontSize: "0.85rem" }}>
          DXC AI Solutions Portal
        </p>
        {error && (
          <div style={{
            background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA",
            borderRadius: 8, padding: "8px 12px", fontSize: "0.82rem", marginBottom: "1rem",
          }}>{error}</div>
        )}
        <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: DXC.textMid, marginBottom: 4 }}>
          Email
        </label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${DXC.lightGray}`,
            fontSize: "0.9rem", marginBottom: "1rem", boxSizing: "border-box",
          }} />
        <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: DXC.textMid, marginBottom: 4 }}>
          Password
        </label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${DXC.lightGray}`,
            fontSize: "0.9rem", marginBottom: "1.5rem", boxSizing: "border-box",
          }} />
        <button type="submit" style={{
          width: "100%", background: DXC.orange, color: DXC.white, border: "none",
          borderRadius: 10, padding: "12px", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
        }}
          onMouseEnter={(e) => (e.currentTarget.style.background = DXC.orangeHover)}
          onMouseLeave={(e) => (e.currentTarget.style.background = DXC.orange)}
        >Login</button>
      </form>
    </div>
  );
}

// ─── Admin Header Bar ──────────────────────────────────────────────────────
function AdminHeaderBar({ onReset, onExport, onLogout, isSuperAdmin, onManageAdmins }) {
  const btnStyle = {
    background: "rgba(255,255,255,0.15)", color: DXC.white, border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: 6, padding: "5px 14px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
  };
  return (
    <div style={{
      background: DXC.navy, padding: "8px 2rem", display: "flex", alignItems: "center",
      justifyContent: "space-between", position: "sticky", top: 0, zIndex: 1100,
      borderBottom: `2px solid ${DXC.orange}`,
    }}>
      <span style={{ color: DXC.orange, fontWeight: 700, fontSize: "0.85rem", letterSpacing: 0.5 }}>
        Admin Mode
      </span>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button onClick={onReset} style={btnStyle}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
        >Reset to defaults</button>
        <button onClick={onExport} style={btnStyle}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
        >Export JSON</button>
        {isSuperAdmin && (
          <button onClick={onManageAdmins} style={btnStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
          >Manage Admins</button>
        )}
        <button onClick={onLogout} style={{ ...btnStyle, background: "rgba(220,38,38,0.8)", borderColor: "rgba(220,38,38,0.6)" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(220,38,38,1)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(220,38,38,0.8)")}
        >Logout</button>
      </div>
    </div>
  );
}

// ─── Array Field (top-level to avoid remount on every render) ─────────────
function ArrayField({ label, path, form, onSet, inputStyle, labelStyle }) {
  const arr = path.split(".").reduce((o, k) => o[k], form);
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {arr.map((item, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
          <input value={item} onChange={(e) => {
            const updated = [...arr];
            updated[i] = e.target.value;
            onSet(path, updated);
          }} style={{ ...inputStyle, flex: 1 }} />
          <button type="button" onClick={() => onSet(path, arr.filter((_, j) => j !== i))}
            style={{
              background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA",
              borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600,
            }}>x</button>
        </div>
      ))}
      <button type="button" onClick={() => onSet(path, [...arr, ""])}
        style={{
          background: DXC.offWhite, color: DXC.textMid, border: `1px solid ${DXC.lightGray}`,
          borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, marginTop: 2,
        }}>+ Add</button>
    </div>
  );
}

// ─── Edit Solution Modal ───────────────────────────────────────────────────
function EditSolutionModal({ solution, onSave, onCancel }) {
  const isNew = !solution;
  const empty = {
    id: 0, rank: "", category: "Consulting", title: "", tags: [],
    mvpDate: "", deliveryDate: "", summary: "",
    popup: {
      headline: "", deliverable: "", stage: "Qualification", description: "",
      businessValue: [], personas: [], featuresDone: [], featuresOngoing: [],
    },
  };
  const [form, setForm] = useState(JSON.parse(JSON.stringify(solution || empty)));

  const set = (path, value) => {
    setForm((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const inputStyle = {
    width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${DXC.lightGray}`,
    fontSize: "0.85rem", boxSizing: "border-box", fontFamily: "inherit",
  };
  const labelStyle = { display: "block", fontSize: "0.78rem", fontWeight: 600, color: DXC.textMid, marginBottom: 3, marginTop: 12 };

  const handleSave = () => {
    const saved = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
    onSave(saved);
  };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)", zIndex: 1200, display: "flex", alignItems: "center",
        justifyContent: "center", padding: "1rem",
      }}>
      <div style={{
        background: DXC.white, borderRadius: 16, width: "100%", maxWidth: 700,
        maxHeight: "92vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
      }}>
        <div style={{
          background: DXC.navy, padding: "1.25rem 1.5rem", borderRadius: "16px 16px 0 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <h2 style={{ color: DXC.white, margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>
            {isNew ? "Add New Solution" : "Edit Solution"}
          </h2>
          <button onClick={onCancel} style={{
            background: "rgba(255,255,255,0.15)", border: "none", color: DXC.white,
            width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 16,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>x</button>
        </div>

        <div style={{ padding: "1.25rem 1.5rem 1.5rem" }}>
          <label style={labelStyle}>Title</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} style={inputStyle} />

          <label style={labelStyle}>Summary</label>
          <textarea value={form.summary} onChange={(e) => set("summary", e.target.value)}
            rows={3} style={{ ...inputStyle, resize: "vertical" }} />

          <label style={labelStyle}>Category</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)}
            style={inputStyle}>
            <option>Consulting</option>
            <option>Delivery</option>
            <option>Innovation</option>
          </select>

          <label style={labelStyle}>Tags (comma-separated)</label>
          <input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
            onChange={(e) => set("tags", e.target.value)} style={inputStyle} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>MVP Date</label>
              <input value={form.mvpDate} onChange={(e) => set("mvpDate", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Delivery Date</label>
              <input value={form.deliveryDate} onChange={(e) => set("deliveryDate", e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{
            borderTop: `1px solid ${DXC.lightGray}`, marginTop: 20, paddingTop: 16,
            fontSize: "0.82rem", fontWeight: 700, color: DXC.orange, textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 4,
          }}>Popup Fields</div>

          <label style={labelStyle}>Headline</label>
          <input value={form.popup.headline} onChange={(e) => set("popup.headline", e.target.value)} style={inputStyle} />

          <label style={labelStyle}>Deliverable</label>
          <input value={form.popup.deliverable} onChange={(e) => set("popup.deliverable", e.target.value)} style={inputStyle} />

          <label style={labelStyle}>Stage</label>
          <select value={form.popup.stage} onChange={(e) => set("popup.stage", e.target.value)} style={inputStyle}>
            <option>Qualification</option>
            <option>Delivery</option>
            <option>Sourcing</option>
          </select>

          <label style={labelStyle}>Description</label>
          <textarea value={form.popup.description} onChange={(e) => set("popup.description", e.target.value)}
            rows={4} style={{ ...inputStyle, resize: "vertical" }} />

          <div style={{
            borderTop: `1px solid ${DXC.lightGray}`, marginTop: 20, paddingTop: 16,
            fontSize: "0.82rem", fontWeight: 700, color: DXC.orange, textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 4,
          }}>Array Fields</div>

          <ArrayField label="Business Value" path="popup.businessValue" form={form} onSet={set} inputStyle={inputStyle} labelStyle={labelStyle} />
          <ArrayField label="Personas" path="popup.personas" form={form} onSet={set} inputStyle={inputStyle} labelStyle={labelStyle} />
          <ArrayField label="Features Done" path="popup.featuresDone" form={form} onSet={set} inputStyle={inputStyle} labelStyle={labelStyle} />
          <ArrayField label="Features Ongoing" path="popup.featuresOngoing" form={form} onSet={set} inputStyle={inputStyle} labelStyle={labelStyle} />

          <div style={{ display: "flex", gap: "0.75rem", marginTop: 24 }}>
            <button onClick={handleSave} style={{
              flex: 1, background: DXC.orange, color: DXC.white, border: "none",
              borderRadius: 10, padding: "12px", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
            }}
              onMouseEnter={(e) => (e.currentTarget.style.background = DXC.orangeHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = DXC.orange)}
            >Save</button>
            <button onClick={onCancel} style={{
              background: DXC.offWhite, color: DXC.textMid, border: `1px solid ${DXC.lightGray}`,
              borderRadius: 10, padding: "12px 24px", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
            }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Learn More Modal ──────────────────────────────────────────────────────
function LearnMoreModal({ solution, allSolutions, onClose, onNavigate }) {
  const touchStartX = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate(1);
      if (e.key === "ArrowLeft") onNavigate(-1);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, onNavigate]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) onNavigate(diff > 0 ? 1 : -1);
    touchStartX.current = null;
  };

  if (!solution) return null;
  const { popup, category } = solution;
  const currentIdx = allSolutions.findIndex(s => s.id === solution.id);
  const stageMap = { Delivery: DXC.delivery, Sourcing: DXC.sourcing, Qualification: DXC.qualification };
  const stage = stageMap[popup.stage] || DXC.delivery;

  const sectionHeader = (label) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10,
      fontSize: 11, fontWeight: 700, color: "#AAA", textTransform: "uppercase",
      letterSpacing: "1.5px", marginBottom: 16,
      borderLeft: "3px solid #FF6200", paddingLeft: 10 }}>
      {label}
    </div>
  );

  return (
    <>
      <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(4px)", zIndex: 1000, display: "flex",
          alignItems: "center", justifyContent: "center", padding: "1rem",
          animation: "fadeIn 0.18s ease" }}>

        <div
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{ background: DXC.white, borderRadius: 20, width: "100%",
            maxWidth: 750, maxHeight: "90vh", overflowY: "auto",
            boxShadow: "0 32px 80px rgba(0,0,0,0.25)", animation: "slideUp 0.22s ease" }}>

          <div style={{ height: 5, background: DXC.orange, borderRadius: "20px 20px 0 0" }} />

          {/* Header */}
          <div style={{ background: `linear-gradient(135deg, ${DXC.navy} 0%, ${DXC.navyMid} 100%)`,
            padding: "1.75rem 2rem", position: "relative" }}>
            <button onClick={onClose}
              style={{ position: "absolute", top: 16, right: 16,
                background: "rgba(255,255,255,0.15)", border: "none",
                color: DXC.white, width: 36, height: 36, borderRadius: "50%",
                cursor: "pointer", fontSize: 18, display: "flex",
                alignItems: "center", justifyContent: "center" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
            >x</button>

            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ background: DXC.orange, color: DXC.white,
                fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>
                {category}
              </span>
              <span style={{ background: stage.bg, color: stage.text,
                border: `1px solid ${stage.border}`,
                fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>
                IPM: {popup.stage}
              </span>
              <span style={{ marginLeft: "auto", color: "rgba(255,255,255,0.4)", fontSize: 11 }}>
                {currentIdx + 1} / {allSolutions.length}
              </span>
            </div>

            <h2 style={{ color: DXC.white, margin: 0, fontSize: "1.5rem",
              fontWeight: 700, lineHeight: 1.2 }}>{popup.headline}</h2>
          </div>

          <div style={{ padding: "2rem" }}>

            {/* SECTION 1 — DESCRIPTION */}
            <div style={{ marginBottom: 24 }}>
              {sectionHeader("DESCRIPTION")}
              <p style={{ color: "#444", lineHeight: 1.75, margin: 0,
                fontSize: 15, padding: "0 0 0 0" }}>{popup.description}</p>
            </div>

            {/* SECTION 2 — BUSINESS VALUE */}
            {popup.businessValue && popup.businessValue.length > 0 && (
              <div style={{ borderTop: "1px solid #F5F5F5", paddingTop: 24, marginBottom: 24 }}>
                {sectionHeader("BUSINESS VALUE")}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {popup.businessValue.map((bv, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start",
                      gap: 10, background: "#FFF7ED", borderRadius: 8,
                      padding: "10px 14px", fontSize: "0.85rem", color: "#444",
                      border: "1px solid #FED7AA" }}>
                      <span style={{ color: DXC.orange, fontWeight: 700, flexShrink: 0, fontSize: 16, marginTop: -1 }}>&#9670;</span>
                      {bv}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SECTION 3 — PERSONAS */}
            {popup.personas && popup.personas.length > 0 && (
              <div style={{ borderTop: "1px solid #F5F5F5", paddingTop: 24, marginBottom: 24 }}>
                {sectionHeader("PERSONAS")}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {popup.personas.map((p, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start",
                      gap: 10, background: DXC.offWhite, borderRadius: 8,
                      padding: "10px 14px", fontSize: "0.85rem", color: "#444",
                      border: `1px solid ${DXC.lightGray}` }}>
                      <span style={{ color: DXC.navy, fontWeight: 700, flexShrink: 0, fontSize: 15 }}>&#9679;</span>
                      {p}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SECTION 4 — KEY FEATURES */}
            <div style={{ borderTop: "1px solid #F5F5F5", paddingTop: 24, marginBottom: 24 }}>
              {sectionHeader("KEY FEATURES")}

              {/* Features Done */}
              {popup.featuresDone && popup.featuresDone.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ background: DXC.greenBg, color: DXC.green,
                      border: `1px solid ${DXC.greenBorder}`,
                      fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20 }}>
                      DONE
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                    {popup.featuresDone.map((f, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start",
                        gap: 8, background: DXC.greenBg, borderRadius: 8,
                        padding: "8px 12px", fontSize: "0.82rem", color: "#444",
                        border: `1px solid ${DXC.greenBorder}` }}>
                        <span style={{ color: DXC.green, fontWeight: 700, flexShrink: 0 }}>&#10003;</span>{f}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Features On Going */}
              {popup.featuresOngoing && popup.featuresOngoing.length > 0 && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ background: DXC.blueBg, color: DXC.blue,
                      border: `1px solid ${DXC.blueBorder}`,
                      fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20 }}>
                      ON GOING
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                    {popup.featuresOngoing.map((f, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start",
                        gap: 8, background: DXC.blueBg, borderRadius: 8,
                        padding: "8px 12px", fontSize: "0.82rem", color: "#444",
                        border: `1px solid ${DXC.blueBorder}` }}>
                        <span style={{ color: DXC.blue, fontWeight: 700, flexShrink: 0 }}>&#8635;</span>{f}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              <button style={{ background: DXC.orange, color: DXC.white, border: "none",
                borderRadius: 10, padding: "12px 24px", fontWeight: 700,
                fontSize: "0.9rem", cursor: "pointer", flex: 1, minWidth: 160 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = DXC.orangeHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = DXC.orange)}
              >Book Discovery Call</button>
              <button onClick={onClose}
                style={{ background: DXC.offWhite, color: DXC.textMid,
                  border: `1px solid ${DXC.lightGray}`, borderRadius: 10,
                  padding: "12px 24px", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = DXC.lightGray)}
                onMouseLeave={(e) => (e.currentTarget.style.background = DXC.offWhite)}
              >Close</button>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(24px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
    </>
  );
}

// ─── Solution Card ─────────────────────────────────────────────────────────
function SolutionCard({ solution, onLearnMore, isAdmin, onEdit, onDelete, onMoveLeft, onMoveRight }) {
  const { title, tags, summary } = solution;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: DXC.white,
        borderRadius: 12,
        border: `1px solid ${DXC.lightGray}`,
        borderLeft: `4px solid ${hovered ? DXC.orange : "transparent"}`,
        display: "flex", flexDirection: "column",
        overflow: "hidden", position: "relative",
        transition: "box-shadow 0.2s, transform 0.2s, border-left-color 0.15s",
        transform: hovered ? "translateY(-2px)" : "none",
        boxShadow: hovered ? "0 8px 28px rgba(0,0,0,0.10)" : "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      {/* Admin overlay toolbar */}
      {isAdmin && hovered && (
        <div style={{
          position: "absolute", top: 8, right: 8, display: "flex", gap: 4, zIndex: 10,
        }}>
          <button onClick={(e) => { e.stopPropagation(); onMoveLeft(); }} title="Move left"
            style={{
              background: DXC.white, border: `1px solid ${DXC.lightGray}`, borderRadius: 6,
              width: 28, height: 28, cursor: "pointer", fontSize: 13, display: "flex",
              alignItems: "center", justifyContent: "center", color: DXC.textMid,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = DXC.offWhite)}
            onMouseLeave={(e) => (e.currentTarget.style.background = DXC.white)}
          >&larr;</button>
          <button onClick={(e) => { e.stopPropagation(); onMoveRight(); }} title="Move right"
            style={{
              background: DXC.white, border: `1px solid ${DXC.lightGray}`, borderRadius: 6,
              width: 28, height: 28, cursor: "pointer", fontSize: 13, display: "flex",
              alignItems: "center", justifyContent: "center", color: DXC.textMid,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = DXC.offWhite)}
            onMouseLeave={(e) => (e.currentTarget.style.background = DXC.white)}
          >&rarr;</button>
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit"
            style={{
              background: DXC.blueBg, border: `1px solid ${DXC.blueBorder}`, borderRadius: 6,
              width: 28, height: 28, cursor: "pointer", fontSize: 13, display: "flex",
              alignItems: "center", justifyContent: "center",
            }}>&#9998;</button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete"
            style={{
              background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6,
              width: 28, height: 28, cursor: "pointer", fontSize: 13, display: "flex",
              alignItems: "center", justifyContent: "center", color: "#DC2626",
            }}>&#10005;</button>
        </div>
      )}

      <div style={{ padding: "1.1rem 1.1rem 0.75rem", flex: 1,
        display: "flex", flexDirection: "column", gap: 10 }}>

        <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700,
          color: DXC.textDark, lineHeight: 1.3 }}>{title}</h3>

        <p style={{ margin: 0, fontSize: "0.81rem", color: DXC.textMid,
          lineHeight: 1.55, flexGrow: 1 }}>{summary}</p>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {tags.map((t) => <TagChip key={t} label={t} />)}
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${DXC.lightGray}` }}>
        <button
          onClick={() => onLearnMore(solution)}
          style={{ width: "100%", background: "transparent", border: "none",
            borderTop: `1px solid ${DXC.lightGray}`, padding: "9px 12px",
            fontSize: "0.82rem", fontWeight: 600, color: DXC.textMid,
            cursor: "pointer", transition: "all 0.18s", textAlign: "center" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = DXC.orange;
            e.currentTarget.style.color = DXC.white;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = DXC.textMid;
          }}
        >
          Learn More &rarr;
        </button>
      </div>
    </div>
  );
}

// ─── Helper: load sub-admins (used only during login before App state is available)
function loadSubAdminsFromFirestore() {
  return getDoc(APP_DOC).then((snap) => {
    if (snap.exists() && snap.data().subAdmins) return snap.data().subAdmins;
    return [];
  }).catch(() => []);
}

// ─── Sub-Admin Management Modal ──────────────────────────────────────────
function SubAdminModal({ onClose, subAdmins, onSubAdminsChange }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleDelete = (emailToDelete) => {
    if (window.confirm(`Remove sub-admin ${emailToDelete}?`)) {
      onSubAdminsChange(subAdmins.filter((a) => a.email !== emailToDelete));
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) { setError("Email is required"); return; }
    if (trimmedEmail === ADMIN_EMAIL.toLowerCase()) { setError("Cannot add the super admin as a sub-admin"); return; }
    if (subAdmins.some((a) => a.email.toLowerCase() === trimmedEmail)) { setError("This email already exists"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    onSubAdminsChange([...subAdmins, { email: trimmedEmail, password, createdAt: new Date().toISOString().slice(0, 10) }]);
    setEmail(""); setPassword(""); setConfirmPassword("");
    setSuccess("Sub-admin added");
    setTimeout(() => setSuccess(""), 3000);
  };

  const inputStyle = {
    width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${DXC.lightGray}`,
    fontSize: "0.85rem", boxSizing: "border-box", fontFamily: "inherit",
  };
  const labelStyle = { display: "block", fontSize: "0.78rem", fontWeight: 600, color: DXC.textMid, marginBottom: 3, marginTop: 12 };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)", zIndex: 1200, display: "flex", alignItems: "center",
        justifyContent: "center", padding: "1rem",
      }}>
      <div style={{
        background: DXC.white, borderRadius: 16, width: "100%", maxWidth: 540,
        maxHeight: "92vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
      }}>
        <div style={{
          background: DXC.navy, padding: "1.25rem 1.5rem", borderRadius: "16px 16px 0 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <h2 style={{ color: DXC.white, margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>
            Sub-Admin Management
          </h2>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.15)", border: "none", color: DXC.white,
            width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 16,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>x</button>
        </div>

        <div style={{ padding: "1.25rem 1.5rem 1.5rem" }}>
          {/* Current sub-admins */}
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: DXC.orange, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            Current Sub-Admins
          </div>
          {subAdmins.length === 0 ? (
            <p style={{ color: DXC.textLight, fontSize: "0.85rem", margin: "0 0 20px" }}>No sub-admins yet</p>
          ) : (
            <div style={{ marginBottom: 20 }}>
              {subAdmins.map((a) => (
                <div key={a.email} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 12px", borderRadius: 8, border: `1px solid ${DXC.lightGray}`,
                  marginBottom: 6, fontSize: "0.85rem",
                }}>
                  <div>
                    <span style={{ fontWeight: 600, color: DXC.textDark }}>{a.email}</span>
                    <span style={{ color: DXC.textLight, marginLeft: 10, fontSize: "0.78rem" }}>Added {a.createdAt}</span>
                  </div>
                  <button onClick={() => handleDelete(a.email)} style={{
                    background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA",
                    borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600,
                  }}>x</button>
                </div>
              ))}
            </div>
          )}

          {/* Add new sub-admin */}
          <div style={{
            borderTop: `1px solid ${DXC.lightGray}`, paddingTop: 16,
            fontSize: "0.82rem", fontWeight: 700, color: DXC.orange, textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 4,
          }}>Add New Sub-Admin</div>

          <form onSubmit={handleAdd}>
            <label style={labelStyle}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />

            <label style={labelStyle}>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />

            <label style={labelStyle}>Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={inputStyle} />

            {error && (
              <div style={{ color: "#DC2626", fontSize: "0.82rem", marginTop: 8 }}>{error}</div>
            )}
            {success && (
              <div style={{ color: DXC.green, fontSize: "0.82rem", marginTop: 8 }}>{success}</div>
            )}

            <button type="submit" style={{
              marginTop: 16, width: "100%", background: DXC.orange, color: DXC.white, border: "none",
              borderRadius: 10, padding: "10px", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
            }}
              onMouseEnter={(e) => (e.currentTarget.style.background = DXC.orangeHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = DXC.orange)}
            >Add Sub-Admin</button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Main App ──────────────────────────────────────────────────────────────
export default function App() {
  const [solutions, setSolutions] = useState(null); // null = loading
  const [subAdmins, setSubAdmins] = useState([]);
  const [filter, setFilter] = useState("All");
  const [activeSolution, setActiveSolution] = useState(null);
  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem("dxc_admin") === "true");
  const [showLogin, setShowLogin] = useState(false);
  const [editingSolution, setEditingSolution] = useState(undefined); // undefined = closed, null = new, object = editing
  const isSuperAdmin = isAdmin && localStorage.getItem("dxc_admin_email") === ADMIN_EMAIL;
  const [showSubAdminModal, setShowSubAdminModal] = useState(false);
  const [offline, setOffline] = useState(false);

  // Load data from Firestore on mount
  useEffect(() => {
    getDoc(APP_DOC).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setSolutions(data.solutions || JSON.parse(JSON.stringify(INITIAL_SOLUTIONS)));
        setSubAdmins(data.subAdmins || []);
      } else {
        const defaults = JSON.parse(JSON.stringify(INITIAL_SOLUTIONS));
        setSolutions(defaults);
        setSubAdmins([]);
        setDoc(APP_DOC, { solutions: defaults, subAdmins: [] }).catch(() => {});
      }
    }).catch(() => {
      setSolutions(JSON.parse(JSON.stringify(INITIAL_SOLUTIONS)));
      setSubAdmins([]);
      setOffline(true);
    });
  }, []);

  // Listen for hash changes to show admin login
  useEffect(() => {
    const checkHash = () => {
      setShowLogin(window.location.hash === "#admin-login");
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  // Persist solutions to Firestore on every change (skip initial null)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (solutions === null) return;
    if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
    saveToFirestore(solutions, subAdmins);
  }, [solutions, subAdmins]);

  const handleLogin = () => {
    setIsAdmin(true);
    setShowLogin(false);
    window.location.hash = "";
  };

  const handleLogout = () => {
    localStorage.removeItem("dxc_admin");
    localStorage.removeItem("dxc_admin_email");
    setIsAdmin(false);
  };

  const handleSubAdminsChange = (updated) => {
    setSubAdmins(updated);
    if (solutions) saveToFirestore(solutions, updated);
  };

  const handleReset = () => {
    if (window.confirm("Reset all solutions to defaults? This will discard all changes.")) {
      const defaults = JSON.parse(JSON.stringify(INITIAL_SOLUTIONS));
      setSolutions(defaults);
      setSubAdmins([]);
      saveToFirestore(defaults, []);
    }
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(solutions, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "solutions.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this solution?")) {
      setSolutions((prev) => prev.filter((s) => s.id !== id));
    }
  };

  const handleSaveEdit = (updated) => {
    setSolutions((prev) => {
      if (prev.find((s) => s.id === updated.id)) {
        return prev.map((s) => (s.id === updated.id ? updated : s));
      }
      // New solution
      const maxId = prev.reduce((max, s) => Math.max(max, s.id), 0);
      const maxRank = prev.reduce((max, s) => Math.max(max, parseInt(s.rank, 10) || 0), 0);
      const newSolution = {
        ...updated,
        id: maxId + 1,
        rank: String(maxRank + 1).padStart(2, "0"),
      };
      return [...prev, newSolution];
    });
    setEditingSolution(undefined);
  };

  const handleReorder = (id, direction) => {
    setSolutions((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx < 0) return prev;
      const item = prev[idx];
      // Find neighbors in same category
      const sameCat = prev.filter((s) => s.category === item.category);
      const catIdx = sameCat.findIndex((s) => s.id === id);
      const targetCatIdx = catIdx + direction;
      if (targetCatIdx < 0 || targetCatIdx >= sameCat.length) return prev;
      const targetId = sameCat[targetCatIdx].id;
      const targetIdx = prev.findIndex((s) => s.id === targetId);
      // Swap in the full array
      const next = [...prev];
      next[idx] = prev[targetIdx];
      next[targetIdx] = prev[idx];
      return next;
    });
  };

  // Loading state while fetching from Firestore
  if (solutions === null) {
    return (
      <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: "100vh",
        background: DXC.offWhite, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: DXC.textMid }}>
          <div style={{ fontSize: "1.5rem", marginBottom: 8 }}>Loading...</div>
          <div style={{ fontSize: "0.85rem", color: DXC.textLight }}>Fetching solutions data</div>
        </div>
      </div>
    );
  }

  // Show admin login page
  if (showLogin && !isAdmin) {
    return <AdminLoginPage onLogin={handleLogin} />;
  }

  const filters = ["All", "Consulting", "Delivery", "Innovation"];
  const counts = { All: solutions.length };
  solutions.forEach((s) => { counts[s.category] = (counts[s.category] || 0) + 1; });
  const filtered = filter === "All" ? solutions : solutions.filter((s) => s.category === filter);

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif",
      minHeight: "100vh", background: DXC.offWhite }}>

      {/* Admin Header Bar */}
      {isAdmin && (
        <AdminHeaderBar onReset={handleReset} onExport={handleExport} onLogout={handleLogout}
          isSuperAdmin={isSuperAdmin} onManageAdmins={() => setShowSubAdminModal(true)} />
      )}

      {/* Offline warning */}
      {offline && (
        <div style={{ background: "#FEF3C7", color: "#92400E", textAlign: "center",
          padding: "6px 16px", fontSize: "0.82rem", fontWeight: 600, borderBottom: "1px solid #FDE68A" }}>
          Offline mode — changes won't be saved
        </div>
      )}

      {/* DXC Header */}
      <div style={{ background: `linear-gradient(160deg, ${DXC.navy} 0%, ${DXC.navyMid} 100%)`,
        padding: "48px 2rem", textAlign: "center" }}>
        <div style={{ marginBottom: 16 }}>
          <DXCLogo size={140} />
        </div>
        <h1 style={{ color: DXC.white, margin: "0 0 10px",
          fontSize: "2rem", fontWeight: 800 }}>
          AI ACCELERATOR PRODUCTS
        </h1>
        <p style={{ color: "rgba(255,255,255,0.65)", margin: 0,
          fontSize: "0.95rem", maxWidth: 520, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
          AI powered products turning technology into business value driven delivery for clients.
        </p>
      </div>

      {/* Page Content */}
      <div style={{ padding: "2rem", maxWidth: 1280, margin: "0 auto" }}>

        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
          <p style={{ margin: 0, color: DXC.textMid, fontSize: "0.9rem" }}>
            Showing <strong style={{ color: DXC.textDark }}>{filtered.length}</strong> of {solutions.length} solutions
            {filter !== "All" && <> &middot; <span style={{ color: DXC.orange, fontWeight: 700 }}>{filter}</span></>}
          </p>
        </div>

        {/* Filter Tabs */}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.75rem", flexWrap: "wrap", alignItems: "center" }}>
          {filters.map((f) => {
            const active = filter === f;
            return (
              <button key={f} onClick={() => setFilter(f)}
                style={{
                  background: active ? DXC.navy : DXC.white,
                  color: active ? DXC.white : DXC.textMid,
                  border: active ? `2px solid ${DXC.navy}` : `2px solid ${DXC.lightGray}`,
                  borderRadius: 24, padding: "6px 18px", fontWeight: 700,
                  fontSize: "0.82rem", cursor: "pointer", transition: "all 0.18s",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                {f}
                <span style={{ background: active ? "rgba(255,255,255,0.2)" : DXC.offWhite,
                  borderRadius: 12, padding: "1px 7px", fontSize: "0.75rem" }}>
                  {counts[f] || 0}
                </span>
              </button>
            );
          })}

          {/* Add Solution Button */}
          {isAdmin && (
            <button onClick={() => setEditingSolution(null)}
              style={{
                background: DXC.orange, color: DXC.white, border: "none",
                borderRadius: 24, padding: "6px 18px", fontWeight: 700,
                fontSize: "0.82rem", cursor: "pointer", marginLeft: "auto",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = DXC.orangeHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = DXC.orange)}
            >+ Add Solution</button>
          )}
        </div>

        {/* Grid */}
        <div style={{ display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: "1.25rem", marginBottom: "3rem" }}>
          {filtered.map((s) => (
            <SolutionCard key={s.id} solution={s}
              onLearnMore={setActiveSolution}
              isAdmin={isAdmin}
              onEdit={() => setEditingSolution(s)}
              onDelete={() => handleDelete(s.id)}
              onMoveLeft={() => handleReorder(s.id, -1)}
              onMoveRight={() => handleReorder(s.id, 1)}
            />
          ))}
        </div>

        {/* CTA Banner */}
        <div style={{ background: DXC.navy, borderRadius: 20, padding: "2rem 2.5rem",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: "1.5rem", marginBottom: "2rem" }}>
          <div>
            <p style={{ color: DXC.white, fontWeight: 800, margin: "0 0 6px", fontSize: "1.1rem" }}>
              Ready to act ?
            </p>
            <p style={{ color: "rgba(255,255,255,0.7)", margin: 0,
              fontSize: "0.88rem", maxWidth: 460, lineHeight: 1.6 }}>
              Our DXC Data &amp; AI team offers a tailored 30-minute discovery session
              to align the right solutions with your priorities.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <button style={{ background: DXC.orange, color: DXC.white, border: "none",
              borderRadius: 10, padding: "12px 24px", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = DXC.orangeHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = DXC.orange)}
            >Book Discovery Call</button>
          </div>
        </div>

        <div style={{ textAlign: "center", color: DXC.textLight, fontSize: 12, paddingBottom: "1rem" }}>
          &copy; 2026 DXC Technology Company. All rights reserved.
        </div>
      </div>

      {activeSolution && (
        <LearnMoreModal
          solution={activeSolution}
          allSolutions={filtered}
          onClose={() => setActiveSolution(null)}
          onNavigate={(dir) => {
            const idx = filtered.findIndex(s => s.id === activeSolution.id);
            const next = filtered[idx + dir];
            if (next) setActiveSolution(next);
          }}
        />
      )}

      {editingSolution !== undefined && (
        <EditSolutionModal
          solution={editingSolution}
          onSave={handleSaveEdit}
          onCancel={() => setEditingSolution(undefined)}
        />
      )}

      {showSubAdminModal && (
        <SubAdminModal onClose={() => setShowSubAdminModal(false)}
          subAdmins={subAdmins} onSubAdminsChange={handleSubAdminsChange} />
      )}
    </div>
  );
}
